/**
 * Copy for the Always Relevant, Cascading Mentorship, and Supervised AI
 * sections on /how-it-works. FLOW_STEPS is shared by the model strip and
 * the supervised-AI chain. Single generic briefing — no partner names.
 */


export const DEFAULT_DOMAIN_LABELS = [
    "Performance Mindset",
    "Emotional Regulation",
    "Self Belief",
    "Resilience & Motivation",
    "Concentration",
    "Leadership",
    "Mental Well-being",
    "Communication",
] as const;

const SCREENSHOT_BASE =
    "https://storage.googleapis.com/strongmsp-media/media/simulator/full_program/tablet/screenshots";

export type FlowAccent = "info" | "primary" | "warning" | "secondary" | "success";

export interface FlowShot {
    src: string;
    alt: string;
    objectPosition: string;
}

export interface FlowStep {
    id: string;
    step: string;
    title: string;
    caption: string;
    body: string;
    accent: FlowAccent;
    highlighted?: boolean;
    shots: FlowShot[];
}

export const FLOW_STEPS: FlowStep[] = [
    {
        id: "assess",
        step: "Step 01",
        title: "Assess",
        caption: "Text or Voice Baseline",
        body: "The participant completes a text or voice assessment. Answers score across the labeled domains and become the first input to generation.",
        accent: "info",
        shots: [
            { src: `${SCREENSHOT_BASE}/step_011_athlete_end.png`, alt: "Assessment Complete", objectPosition: "50% 6%" },
            { src: `${SCREENSHOT_BASE}/step_017_athlete_end.png`, alt: "Baseline Domain Scores", objectPosition: "50% 6%" },
            { src: `${SCREENSHOT_BASE}/step_007_athlete_end.png`, alt: "50-Question Assessment & Voice Option", objectPosition: "50% 7%" },
        ],
    },
    {
        id: "reflect",
        step: "Step 02",
        title: "Reflect",
        caption: "Feedback & Prompts",
        body: "AI drafts the feedback report and coach-internal talking points. A coach can edit or send a change request, then publishes before anyone else sees the work.",
        accent: "primary",
        shots: [
            { src: `${SCREENSHOT_BASE}/step_062_athlete_end.png`, alt: "Final Program Reflection", objectPosition: "50% 8%" },
            { src: `${SCREENSHOT_BASE}/step_030_athlete_end.png`, alt: "Session Reflection Prompt", objectPosition: "50% 14%" },
            { src: `${SCREENSHOT_BASE}/step_018_athlete_end.png`, alt: "Personalized Feedback Report", objectPosition: "50% 7%" },
        ],
    },
    {
        id: "apply",
        step: "Step 03",
        title: "Apply",
        caption: "12-Session Curriculum",
        body: "AI drafts the 12-session curriculum and lesson-plan slides. Published sessions run with a peer, a coach, or AI — scripts and coaching tips already written for that person.",
        accent: "warning",
        shots: [
            { src: `${SCREENSHOT_BASE}/step_027_coach_end.png`, alt: "Session Scheduling", objectPosition: "50% 6%" },
            { src: `${SCREENSHOT_BASE}/step_022_athlete_end.png`, alt: "12-Session Curriculum Plan", objectPosition: "50% 3%" },
            { src: `${SCREENSHOT_BASE}/step_031_coach_end.png`, alt: "Interactive 12-Session Lesson Plan", objectPosition: "50% 10%" },
        ],
    },
    {
        id: "observe",
        step: "Step 04",
        title: "Observe",
        caption: "Coach & Guardian Notes",
        body: "Coaches and guardians add session notes and complete checkpoint surveys at weeks 4, 8, and 12. Those observations feed the next draft without exposing private reflections.",
        accent: "success",
        shots: [
            { src: `${SCREENSHOT_BASE}/step_038_coach_start.png`, alt: "Coach Assignment Dashboard", objectPosition: "50% 6%" },
            { src: `${SCREENSHOT_BASE}/step_040_guardian_end.png`, alt: "Week 4 Observer Checkpoint Survey", objectPosition: "50% 20%" },
            { src: `${SCREENSHOT_BASE}/step_033_guardian_end.png`, alt: "Mentor & Guardian Session Notes", objectPosition: "50% 14%" },
        ],
    },
    {
        id: "measure",
        step: "Step 05",
        title: "Measure",
        caption: "Pre → Post Growth Report",
        body: "Growth reports land at weeks 4, 8, and 12 — numbers computed, short narratives drafted. A closing post-assessment completes the pre → post picture; the coach publishes.",
        accent: "success",
        highlighted: true,
        shots: [
            { src: `${SCREENSHOT_BASE}/step_069_athlete_end.png`, alt: "Domain Score Deltas Table", objectPosition: "50% 62%" },
            { src: `${SCREENSHOT_BASE}/step_069_athlete_end.png`, alt: "Pre vs Post Radar Comparison", objectPosition: "50% 8%" },
            { src: `${SCREENSHOT_BASE}/step_067_athlete_end.png`, alt: "Week 12 Growth Report & Benchmark", objectPosition: "50% 7%" },
        ],
    },
];
